<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import CollapseExpandAllButton from "@/components/episto-ui/collapse-card/CollapseExpandAllButton.vue";
  import { storeToRefs } from "pinia";
  import useMyFetch from "@stores/useMyFetch";
  import { useQuotasStore } from "@stores/survey/quotas";
  import { useCardsStore } from "@stores/survey/cards";
  import OnePanePage from "@/templates/OnePanePage.vue";
  import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import SearchInput from "@/components/episto-ui/search-input/SearchInput.vue";
  import { ProgressTargetCard, buildRows, type QuotaStatus } from "@/components/Survey/Progress/ProgressCards";
  import { useCardAttributes } from "@/utils/composables/questionCard";
  import { stringParam, useFiltersUrlSync } from "@/utils/composables/filters/useFiltersUrlSync";
  import type { Survey, SurveyProgress } from "@/types";

  const props = defineProps<{
    survey: Survey;
  }>();

  const { answerTitle } = useQuotasStore();
  const { cards } = storeToRefs(useCardsStore());
  const { cardsTitle } = useCardAttributes();

  const progress = ref<SurveyProgress | null>(null);

  onMounted(async () => {
    const { data } = await useMyFetch(`surveys/${props.survey.token}/progress`).json<SurveyProgress>();
    progress.value = data.value;
  });

  const targets = computed(() => {
    return (progress.value?.targets ?? []).map((target) => ({
      ...target,
      questions: target.questions.map((question) => {
        const card = cards.value.find((c) => c.token === question.card_token);
        return {
        ...question,
        name: card?.name,
        title: card ? cardsTitle(card) : "",
        quotas: question.quotas.map((quota) => ({
          ...quota,
          name: answerTitle(question.card_token, quota.answer),
        })),
        };
      }),
    }));
  });

  const rows = computed(() => buildRows(targets.value));

  const searchQuery = ref("");
  const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());

  type StatusFilter = "all" | Extract<QuotaStatus, "below_min" | "full">;
  const statusFilter = ref<StatusFilter>("all");
  const toStatusFilter = (value: string): StatusFilter =>
    value === "below_min" || value === "full" ? value : "all";

  const countedCells = computed(() =>
    rows.value
      .filter((row) => row.id !== "global")
      .flatMap((row) => row.questions.flatMap((question) => question.quotas)),
  );

  const statusTabs = computed(() => [
    { value: "all", label: "All cells", count: countedCells.value.length },
    {
      value: "below_min",
      label: "Below min",
      count: countedCells.value.filter((quota) => quota.status === "below_min").length,
    },
    { value: "full", label: "Full", count: countedCells.value.filter((quota) => quota.status === "full").length },
  ]);

  // In the URL so a filtered view can be shared, and so the assistant opened
  // from this page knows which questions are actually on screen.
  const { syncUrl, restoreFromUrl } = useFiltersUrlSync({
    params: { search: stringParam(), status: stringParam() },
    serialize: () => ({
      search: searchQuery.value.trim(),
      status: statusFilter.value === "all" ? "" : statusFilter.value,
    }),
    apply: ({ search, status }) => {
      searchQuery.value = search;
      statusFilter.value = toStatusFilter(status);
    },
    onExternalChange: ({ search, status }) => {
      searchQuery.value = search;
      statusFilter.value = toStatusFilter(status);
    },
  });

  restoreFromUrl();

  const filteredRows = computed(() => {
    const query = normalizedQuery.value;
    const status = statusFilter.value;
    if (!query && status === "all") return rows.value;
    return rows.value
      .map((row) => ({
        ...row,
        questions: row.questions
          .filter(
            (question) =>
              !query || `${question.name} ${question.title}`.toLowerCase().includes(query),
          )
          .map((question) => ({
            ...question,
            quotas: status === "all" ? question.quotas : question.quotas.filter((quota) => quota.status === status),
          }))
          .filter((question) => question.quotas.length > 0),
      }))
      .filter((row) => row.questions.length > 0);
  });

  const isFiltering = computed(() => !!normalizedQuery.value || statusFilter.value !== "all");

  const expandedIds = ref<string[]>([]);

  const allExpanded = computed(
    () => filteredRows.value.length > 0 && filteredRows.value.every((row) => expandedIds.value.includes(row.id)),
  );

  const handleToggle = (id: string) => {
    const set = new Set(expandedIds.value);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    expandedIds.value = Array.from(set);
  };

  const expandAll = () => (expandedIds.value = filteredRows.value.map((row) => row.id));
  const collapseAll = () => (expandedIds.value = []);

  watch(
    rows,
    (newRows) => {
      if (newRows.length === 1 && newRows[0].hasQuotas) expandedIds.value = [newRows[0].id];
    },
    { immediate: true },
  );

  watch([normalizedQuery, statusFilter], () => {
    expandedIds.value = isFiltering.value ? filteredRows.value.map((row) => row.id) : [];
    syncUrl();
  });
</script>

<template>
  <OnePanePage
    :span-center="10"
    data-cy="survey-progress-page">
    <div
      v-if="rows.length"
      class="mb-4 flex items-center justify-between gap-4">
      <Tabs
        :model-value="statusFilter"
        @update:model-value="(value) => (statusFilter = toStatusFilter(String(value)))">
        <TabsList>
          <TabsTrigger
            v-for="tab in statusTabs"
            :key="tab.value"
            :value="tab.value"
            class="px-3">
            {{ tab.label }}
            <span class="tabular-nums text-foreground-subtle">{{ tab.count }}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div class="flex items-center gap-2">
        <SearchInput
          v-model="searchQuery"
          size="sm"
          placeholder="Search a question..." />
        <CollapseExpandAllButton
          :expanded="allExpanded"
          size="sm"
          @toggle="allExpanded ? collapseAll() : expandAll()" />
      </div>
    </div>

    <ProgressTargetCard
      v-for="row in filteredRows"
      :key="row.id"
      :target="row"
      :visible-collapse="expandedIds"
      @toggle="handleToggle" />

    <EmptyState
      v-if="progress && !filteredRows.length"
      size="lg"
      :title="isFiltering ? 'No cells match these filters.' : 'No progress data available.'" />
  </OnePanePage>
</template>
